import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import analyticsService from '../../services/analyticsService';
import type { AnalyticsFilters, StreaksResponse } from '../../types/analytics';

const StreakPanel: React.FC<{ filters: AnalyticsFilters }> = ({ filters }) => {
  const [data, setData] = useState<StreaksResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsService.getStreaks(filters).then(r => {
      if (!cancelled) setData(r);
    }).catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [filters]);

  if (!data) return null;

  const Card: React.FC<{
    label: string; value: string; icon: React.ReactNode; valueClass?: string;
  }> = ({ label, value, icon, valueClass }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-3">
      <div className="bg-gray-100 dark:bg-gray-700 w-10 h-10 rounded-lg flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
        <div className={`text-xl font-bold mt-0.5 tabular-nums ${valueClass ?? 'text-gray-900 dark:text-white'}`}>
          {value}
        </div>
      </div>
    </div>
  );

  const currentLabel = data.currentDirection === 'win'
    ? `${data.currentStreakLength} win${data.currentStreakLength === 1 ? '' : 's'}`
    : data.currentDirection === 'loss'
      ? `${data.currentStreakLength} loss${data.currentStreakLength === 1 ? '' : 'es'}`
      : 'None';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Card label="Longest Win Streak"
        value={String(data.longestWinStreak)}
        icon={<TrendingUp className="w-5 h-5 text-green-600" />}
        valueClass="text-green-600 dark:text-green-400"
      />
      <Card label="Longest Loss Streak"
        value={String(data.longestLossStreak)}
        icon={<TrendingDown className="w-5 h-5 text-red-600" />}
        valueClass="text-red-600 dark:text-red-400"
      />
      <Card label="Current"
        value={currentLabel}
        icon={<Activity className="w-5 h-5 text-blue-600" />}
        valueClass={data.currentDirection === 'win' ? 'text-green-600 dark:text-green-400' :
                    data.currentDirection === 'loss' ? 'text-red-600 dark:text-red-400' : ''}
      />
    </div>
  );
};

export default StreakPanel;
